import { useApiMutation, useApiQuery, type ApiClient } from "@gladis/api-client";
import type { UseQueryResult } from "@tanstack/react-query";

import type { IncomeGranularity, IncomePageDto } from "./dto.js";

/**
 * The page's one query, and every mutation that invalidates it.
 *
 * ONE QUERY KEY PER (granularity, months) PAIR. The toggle changes the key rather than refetching the
 * same key with different parameters, so switching back to a view already loaded is instant and the
 * two resolutions cannot briefly show each other's data while a refetch is in flight.
 *
 * NO `refetchOnMount`. That exemption belongs to the Expenses page, whose payload contains mortgage
 * interest it does not own and cannot observe changing. Income owns everything it shows, so the
 * global 30-second `staleTime` is correct here and a second fetch on every visit would be waste.
 */
export function incomeQueryKey(granularity: IncomeGranularity, months: number): unknown[] {
  return ["income", granularity, granularity === "month" ? months : "all"];
}

export function useIncomePage(
  granularity: IncomeGranularity,
  months: number,
): UseQueryResult<IncomePageDto> {
  const query =
    granularity === "month" ? `?granularity=month&months=${months}` : "?granularity=year";
  return useApiQuery<IncomePageDto>(incomeQueryKey(granularity, months), `/income${query}`);
}

/**
 * Every write invalidates the whole `income` key rather than the specific pair it was issued from.
 *
 * A raise recorded while the Year view is showing changes the Month view too, and a household that
 * toggles across afterwards should not be looking at a figure from before the raise. The alternative
 * — invalidating only the active pair — is the kind of cache bug that surfaces as a chart quietly
 * disagreeing with the stat card above it.
 */
const INVALIDATES = [["income"]] as const;

export interface CreateSourceInput {
  name: string;
  category: string;
  ownership: string;
  ownerId: string | null;
  amount: string;
  interval: string;
  effectiveFrom: string;
}

export function useCreateIncomeSource() {
  return useApiMutation<{ id: string }, CreateSourceInput>(
    (client: ApiClient, input) => client.post<{ id: string }>("/income/sources", input),
    { invalidates: INVALIDATES },
  );
}

export function useEditIncomeSource() {
  return useApiMutation<
    void,
    { id: string } & Omit<CreateSourceInput, "amount" | "interval" | "effectiveFrom">
  >((client, { id, ...body }) => client.patch<void>(`/income/sources/${id}`, body), {
    invalidates: INVALIDATES,
  });
}

/** `PUT`, because it is an upsert: recording a date the source already has replaces that row. */
export function useRecordIncomeAmount() {
  return useApiMutation<
    void,
    { sourceId: string; amount: string; interval: string; effectiveFrom: string }
  >(
    (client, { sourceId, ...body }) =>
      client.put<void>(`/income/sources/${sourceId}/amounts`, body),
    { invalidates: INVALIDATES },
  );
}

export function useRemoveIncomeAmount() {
  return useApiMutation<void, { sourceId: string; periodId: string }>(
    (client, { sourceId, periodId }) =>
      client.delete<void>(`/income/sources/${sourceId}/amounts/${periodId}`),
    { invalidates: INVALIDATES },
  );
}

/** Separate from archiving, at every level. See `SourceList` for why that separation is visible. */
export function useEndIncomeSource() {
  return useApiMutation<void, { sourceId: string; endedOn: string | null }>(
    (client, { sourceId, endedOn }) =>
      client.post<void>(`/income/sources/${sourceId}/end`, { endedOn }),
    { invalidates: INVALIDATES },
  );
}

export function useArchiveIncomeSource() {
  return useApiMutation<void, { sourceId: string; archived: boolean }>(
    (client, { sourceId, archived }) =>
      client.post<void>(`/income/sources/${sourceId}/archive`, { archived }),
    { invalidates: INVALIDATES },
  );
}

export interface RecordEntryInput {
  incomeSourceId: string | null;
  description: string;
  amount: string;
  receivedOn: string;
  category?: string;
  ownership?: string;
  ownerId?: string | null;
}

export function useRecordIncomeEntry() {
  return useApiMutation<{ id: string }, RecordEntryInput>(
    (client, input) => client.post<{ id: string }>("/income/entries", input),
    { invalidates: INVALIDATES },
  );
}

export function useEditIncomeEntry() {
  return useApiMutation<void, { id: string } & Omit<RecordEntryInput, "incomeSourceId">>(
    (client, { id, ...body }) => client.patch<void>(`/income/entries/${id}`, body),
    { invalidates: INVALIDATES },
  );
}

export function useDeleteIncomeEntry() {
  return useApiMutation<void, { id: string }>(
    (client, { id }) => client.delete<void>(`/income/entries/${id}`),
    { invalidates: INVALIDATES },
  );
}
